import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import {
  EventAvailable as EventIcon,
  Store as VendorIcon,
  LocationOn as LocationIcon,
  LocalOffer as PricingIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import PublicFooter from '../components/layout/PublicFooter';

const LandingPage = () => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMobileMenuClose();
  };

  const features = [
    {
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      title: 'Find Your Community',
      description: 'Discover all the markets and events happening near you. No more jumping between websites or social media—everything you need is right here.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Apply in Seconds',
      description: 'Your info is saved, so applying to events is quick and easy. More time for you, less time filling out forms.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Safe & Trusted',
      description: 'Your information stays private and secure. Built with care by people who understand what vendors need.',
    },
  ];

  const vendorBenefits = [
    'All your local markets in one place—never miss an event',
    'No more checking Facebook, Instagram, and websites separately',
    'See what\'s happening in your community at a glance',
    'Apply to events quickly with your info already saved',
    'Know right away if you got accepted',
    'Get notified when new markets pop up near you',
  ];

  const organizerBenefits = [
    'Post your market or event in minutes',
    'Find amazing local vendors in your area',
    'Review applications quickly and easily',
    'Build your vendor lineup for each event',
    'See who\'s interested in joining your market',
    'Connect directly with vendors in your community',
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          bgcolor: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box
            onClick={() => history.push('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              flexGrow: 1,
            }}
          >
            <EventIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                fontSize: '1.5rem',
              }}
            >
              Vendoroll
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              onClick={() => history.push('/for-vendors')}
              sx={{ 
                color: 'text.primary', 
                textTransform: 'none', 
                fontWeight: 500,
                fontSize: '0.9375rem',
              }}
            >
              For Vendors
            </Button>
            <Button
              color="inherit"
              onClick={() => history.push('/for-organizers')}
              sx={{ 
                color: 'text.primary', 
                textTransform: 'none', 
                fontWeight: 500,
                fontSize: '0.9375rem',
              }}
            >
              For Organizers
            </Button>
            <Button
              color="inherit"
              onClick={() => history.push('/about')}
              sx={{ 
                color: 'text.primary', 
                textTransform: 'none', 
                fontWeight: 500,
                fontSize: '0.9375rem',
              }}
            >
              About
            </Button>
            <Button
              variant="outlined"
              onClick={() => history.push('/login')}
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                textTransform: 'none',
                fontWeight: 600,
                ml: 1,
                px: 2,
                py: 0.75,
                fontSize: '0.9375rem',
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              onClick={() => history.push('/register')}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                ml: 1,
                px: 2,
                py: 0.75,
                fontSize: '0.9375rem',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              Get Started
            </Button>
          </Box>
          
          {/* Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
            >
              <MenuItem onClick={() => handleNavigation('/for-vendors')}>For Vendors</MenuItem>
              <MenuItem onClick={() => handleNavigation('/for-organizers')}>For Organizers</MenuItem>
              <MenuItem onClick={() => handleNavigation('/about')}>About</MenuItem>
              <MenuItem onClick={() => handleNavigation('/login')}>Sign In</MenuItem>
              <MenuItem onClick={() => handleNavigation('/register')}>Get Started</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Beta Now Available"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  mb: 3,
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Your Local Community. All Markets. One Place.
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                Built by vendors, for vendors. We know how hard it is to keep track of all the markets and events happening in your area. That's why we've gathered everything in one place—so you can focus on what you do best, and never miss out on connecting with your community.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => history.push('/register')}
                  endIcon={<ArrowForwardIcon />}
                  fullWidth={isMobile}
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.25, md: 1.5 },
                    borderRadius: 2,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                    minHeight: { xs: 48, md: 56 },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => history.push('/login')}
                  fullWidth={isMobile}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.25, md: 1.5 },
                    borderRadius: 2,
                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    minHeight: { xs: 48, md: 56 },
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  height: { xs: 250, sm: 300, md: 400 },
                  mt: { xs: 4, md: 0 },
                }}
              >
                {/* PC View */}
                <Box
                  sx={{
                    position: 'relative',
                    transform: { xs: 'rotate(-1deg)', md: 'rotate(-2deg)' },
                    transition: 'transform 0.3s ease',
                    zIndex: 1,
                    '&:hover': {
                      transform: { xs: 'rotate(0deg)', md: 'rotate(0deg) translateY(-10px)' },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/images/PC_view.png"
                    alt="Platform Preview"
                    sx={{
                      width: '100%',
                      maxWidth: { xs: 240, sm: 280, md: 350 },
                      height: 'auto',
                      filter: 'drop-shadow(0px 8px 32px rgba(0, 0, 0, 0.2))',
                      borderRadius: 2,
                    }}
                  />
                </Box>

                {/* Phone View with Coming Soon Badge - Overlapping */}
                <Box
                  sx={{
                    position: 'absolute',
                    right: { xs: '0%', sm: '5%', md: '10%' },
                    bottom: { xs: '5%', sm: '10%', md: '15%' },
                    transform: { xs: 'rotate(1deg)', md: 'rotate(2deg)' },
                    transition: 'transform 0.3s ease',
                    zIndex: 2,
                    '&:hover': {
                      transform: { xs: 'rotate(0deg)', md: 'rotate(0deg) translateY(-10px)' },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/images/Phone_view.png"
                    alt="Mobile App Preview"
                    sx={{
                      width: '100%',
                      maxWidth: { xs: 100, sm: 120, md: 160 },
                      height: 'auto',
                      filter: 'drop-shadow(0px 8px 32px rgba(0, 0, 0, 0.2))',
                      borderRadius: 2,
                    }}
                  />
                  <Chip
                    label="Mobile App Coming Soon"
                    sx={{
                      position: 'absolute',
                      top: { xs: -6, md: -8 },
                      right: { xs: -6, md: -8 },
                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.75rem' },
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
              }}
            >
              Built for Your Community
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                px: { xs: 2, md: 0 },
              }}
            >
              Simple tools that help you connect with local markets and events, without the hassle.
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateY(-8px)' },
                      boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', md: '0 12px 40px rgba(0,0,0,0.1)' },
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 64, md: 80 },
                      height: { xs: 64, md: 80 },
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 2, md: 3 },
                      color: 'white',
                      '& svg': {
                        fontSize: { xs: 32, md: 40 },
                      },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* For Vendors Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: 'text.primary',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                }}
              >
                For Vendors
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ 
                  mb: 4, 
                  lineHeight: 1.7,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                }}
              >
                We get it—keeping track of all the markets and events in your area is exhausting. That's why we built this for you. All your local opportunities, all in one place, so you can focus on what you love doing instead of searching for where to do it.
              </Typography>
              <Box sx={{ mb: 4 }}>
                {vendorBenefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, md: 2 } }}>
                    <CheckCircleIcon
                      sx={{
                        color: theme.palette.primary.main,
                        mr: { xs: 1.5, md: 2 },
                        mt: 0.5,
                        fontSize: { xs: 20, md: 24 },
                        flexShrink: 0,
                      }}
                    />
                    <Typography 
                      variant="body1" 
                      color="text.primary"
                      sx={{
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        lineHeight: 1.6,
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => history.push('/register')}
                endIcon={<ArrowForwardIcon />}
                fullWidth={isMobile}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: 2,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: { xs: 48, md: 56 },
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Start as Vendor
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: '#fafafa',
                  borderRadius: 3,
                  p: 4,
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <VendorIcon sx={{ fontSize: 120, color: theme.palette.primary.main, opacity: 0.2, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Your Vendor Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Show off what you do, see all your applications, and find new markets to join—all from one simple place.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* For Organizers Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: 3,
                  p: 4,
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <EventIcon sx={{ fontSize: 120, color: theme.palette.primary.main, opacity: 0.2, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Your Market Hub
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Post your market, see who wants to join, and build your vendor lineup—everything you need to run a great event.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: 'text.primary',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                }}
              >
                For Market Organizers
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ 
                  mb: 4, 
                  lineHeight: 1.7,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                }}
              >
                Connect with amazing local vendors in your community. Post your market or event, review applications, and build your vendor lineup—all in one simple place.
              </Typography>
              <Box sx={{ mb: 4 }}>
                {organizerBenefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, md: 2 } }}>
                    <CheckCircleIcon
                      sx={{
                        color: theme.palette.primary.main,
                        mr: { xs: 1.5, md: 2 },
                        mt: 0.5,
                        fontSize: { xs: 20, md: 24 },
                        flexShrink: 0,
                      }}
                    />
                    <Typography 
                      variant="body1" 
                      color="text.primary"
                      sx={{
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        lineHeight: 1.6,
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => history.push('/register')}
                endIcon={<ArrowForwardIcon />}
                fullWidth={isMobile}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: 2,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: { xs: 48, md: 56 },
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Start as Organizer
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
              }}
            >
              Ready to Join Your Community?
            </Typography>
            <Typography
              variant="h6"
              sx={{ 
                mb: 4, 
                opacity: 0.95,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                px: { xs: 2, md: 0 },
              }}
            >
              Join local vendors and market organizers across Canada who are already connecting through Vendoroll.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', width: '100%' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => history.push('/register')}
                endIcon={<ArrowForwardIcon />}
                fullWidth={isMobile}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  px: { xs: 3, md: 5 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: 2,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: { xs: 48, md: 56 },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                  },
                }}
              >
                Create Free Account
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => history.push('/login')}
                fullWidth={isMobile}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: { xs: 3, md: 5 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: 2,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: { xs: 48, md: 56 },
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <PublicFooter />
    </Box>
  );
};

export default LandingPage;
